import axios from 'axios';
import { API_URL } from '../config';

const client = axios.create({ baseURL: API_URL });

const ACCESS_KEY = 'bit_token';
const REFRESH_KEY = 'bit_refresh';
// Controls which storage the next login writes to. localStorage (true)
// persists across browser restarts for 30d refresh; sessionStorage (false)
// clears when the tab/window closes.
const REMEMBER_KEY = 'bit_remember_me';

function rememberMe() {
  // Default to "remember" if the flag hasn't been set yet (keeps existing
  // sessions working unchanged).
  const v = localStorage.getItem(REMEMBER_KEY);
  return v === null ? true : v !== 'false';
}
function setRememberMe(flag) {
  localStorage.setItem(REMEMBER_KEY, flag ? 'true' : 'false');
}
export { rememberMe, setRememberMe };

function activeStorage() {
  return rememberMe() ? window.localStorage : window.sessionStorage;
}
function readToken(key) {
  // Read from both stores so we tolerate the user toggling the checkbox
  // between sessions (old session-storage token still works until expiry).
  return (
    window.sessionStorage.getItem(key) || window.localStorage.getItem(key)
  );
}
function writeToken(key, value) {
  if (!value) return;
  const target = activeStorage();
  target.setItem(key, value);
  // Clear the OTHER store so we don't ever have two tokens fighting.
  const other = target === window.localStorage ? window.sessionStorage : window.localStorage;
  other.removeItem(key);
}
function removeToken(key) {
  window.localStorage.removeItem(key);
  window.sessionStorage.removeItem(key);
}

export function setTokens({ access_token, refresh_token }) {
  if (access_token) writeToken(ACCESS_KEY, access_token);
  if (refresh_token) writeToken(REFRESH_KEY, refresh_token);
}
export function clearTokens() {
  removeToken(ACCESS_KEY);
  removeToken(REFRESH_KEY);
}

client.interceptors.request.use((cfg) => {
  const token = readToken(ACCESS_KEY);
  if (token) cfg.headers.Authorization = `Bearer ${token}`;
  return cfg;
});

// Silent refresh on 401. Serialises concurrent retries so only one refresh
// request goes out per 401 burst.
let refreshInFlight = null;

async function doRefresh() {
  const refresh_token = readToken(REFRESH_KEY);
  if (!refresh_token) throw new Error('no refresh token');
  const res = await axios.post(`${API_URL}/auth/refresh`, { refresh_token });
  setTokens(res.data);
  return res.data.access_token;
}

// FastAPI emits 422 detail as an array of {type, loc, msg, input, ctx}
// dicts. Pages that surface the message via `ex.response.data.detail`
// (toast, inline error div) cannot render that array as a React child
// (React error #31). Normalize to a single human string so every call
// site stays terse.
function _normalizeDetail(payload) {
  const d = payload?.detail;
  if (Array.isArray(d)) {
    payload.detail = d
      .map((e) => {
        const where = Array.isArray(e?.loc) ? e.loc.join('.') : '';
        const msg = e?.msg || 'invalid';
        return where ? `${where}: ${msg}` : msg;
      })
      .join('; ');
  } else if (d && typeof d === 'object') {
    payload.detail = d.msg || JSON.stringify(d);
  }
}

client.interceptors.response.use(
  (r) => r,
  async (err) => {
    if (err?.response?.data) {
      _normalizeDetail(err.response.data);
    }
    const cfg = err?.config;
    const status = err?.response?.status;
    if (status !== 401 || !cfg || cfg._bitRetry || cfg.url?.includes('/auth/refresh') || cfg.url?.includes('/auth/login')) {
      if (status === 401) {
        clearTokens();
        if (window.location.pathname !== '/login' && window.location.pathname !== '/client/login') {
          window.location.href = '/login';
        }
      }
      return Promise.reject(err);
    }
    try {
      if (!refreshInFlight) refreshInFlight = doRefresh().finally(() => { refreshInFlight = null; });
      const newToken = await refreshInFlight;
      cfg._bitRetry = true;
      cfg.headers = cfg.headers || {};
      cfg.headers.Authorization = `Bearer ${newToken}`;
      return client(cfg);
    } catch {
      clearTokens();
      if (window.location.pathname !== '/login' && window.location.pathname !== '/client/login') {
        window.location.href = '/login';
      }
      return Promise.reject(err);
    }
  }
);

export default client;
